import size from 'lodash/size';
import { Config } from '@/environment';
import { Logger } from '../logger';
import { CustomerProfile, CustomerSubscriptionStatus, OrderWithContext, OrderType, PreConfiguredMeal, RecentOrFavoriteOrderMeal, OrderMealRequest, MealType } from '@/models';
import {
  convertPreconfiguredMealToAnalyticsProducts,
  convertMealsToAnalyticsProducts,
  convertNonFoodItemsToAnalyticsProducts,
  AnalyticsProduct,
  convertMealRequestOrRecentOrFavoriteMealToAnalyticsProducts,
  reduceProductsToString,
} from './google-analytics-data-formatting';
import { MetadataService } from '../metadata/metadata.service';
import { Store } from 'vuex';
import { AppState } from '@/store';
import { GETTERS as CUSTOMER_GETTERS } from '@/store/customer';
import { loadScript } from '@/helpers/util';

export interface GoogleAnalyticsService {
  lastVisitedProductDetailView: string;

  sendCustomEvent(event: CustomAnalyticsEvent): void;
  sendPageViewEvent(page: string): void;
  sendLoyaltyEnrollmentEvent(customer: CustomerProfile | null, pageTitle: string): void;
  sendEcommerceCheckoutEvent(order: OrderWithContext, eventType: string, step: number): Promise<void>;
  sendEcommerceTransactionEvent(order: OrderWithContext, customer: CustomerProfile, enrolledInLoyalty: boolean, currencyCode?: string): Promise<void>;
  preconfigureMealAddedToBag(meal: PreConfiguredMeal): Promise<void>;
  recentOrFavoriteMealAddedToBag(meal: RecentOrFavoriteOrderMeal): void;
  builtMealAddedToBag(meal: OrderMealRequest): void;

  /** Product click event should happen from home page or wherever a top level product is selected */
  sendProductClickEvent(name: string): void;

  /** This event should be fired on load of any of meal builder page to log that somebody looked at product details */
  sendProductDetailViewEvent(productName: string): void;
  trackElementScrollPercentage(element: HTMLElement): void;
  resetLastScrollPercentage(): void;
}

export interface CustomAnalyticsEvent {
  category: string;
  action: string;
  label?: string;
  values?: { [key: string]: string | null };
}

export enum AnalyticsEventCategory {
  Customer = 'customer',
  Rewards = 'rewards',
  Restaurant = 'restaurant',
  Delivery = 'delivery',
  Reorder = 'reorder',
  Featured = 'featured',
  Checkout = 'checkout',
  ECommerce = 'ecommerce',
  Behavior = 'behavior',
  MealBuilder = 'meal-builder',
  RewardsLanding = 'rewards-land',
  RewardsAccount = 'rewards-account',
  RewardsStore = 'rewards-store',
  RewardDetailModal = 'reward-detail-modal',
  RewardPointHistory = 'rewards-point-history',
  RewardGeneralOnboard = 'rewards-general-onboard',
  RewardsHowOnboard = 'rewards-how-onboard',
  RewardsStoreOnboard = 'rewards-store-onboard',
  Confirmation = 'order-confirmation',
  RewardsNonLoyaltyWelcome = 'rewards-nonloyalty-welcome',
  RewardsNotification = 'rewards-notification',
  ContextualBag = 'rewards-context-bag',
}

export enum AnalyticsEventAction {
  CreateAccountSuccess = 'create-account-success',
  SignInSuccess = 'sign-in-success',
  SignOutSuccess = 'sign-out-success',
  ReAuthenticated = 're-authenticated',
  EnrollSuccess = 'enroll-success',
  EnrollPendingSuccess = 'enroll-pending-success',
  UnenrollSuccess = 'unenroll-success',
  SearchSubmit = 'search-submit',
  ErrorMessage = 'error-message',
  AddToBag = 'add-to-bag',
  AddToBagFavorite = 'addtobag-favorite',
  AddToBagRecent = 'addtobag-recent',
  AddToBagNonFoodItem = 'addtobag-non-food-item',
  VirtualPageView = 'virtual-page-view',
  ScrollEvent = 'scroll-tracking',
  RestaurantUpdate = 'restaurant-update',
  Click = 'click',
  Toggle = 'toggle',
  PageViewed = 'page viewed',
  RewardExchangeClick = 'reward exchange cta clicked',
  ViewPointHistoryClick = 'view point history clicked',
  ForgotToScanClick = 'forgot to scan clicked',
  AddRewardClick = 'add reward clicked',
  RewardAdded = ' reward successfully added',
  RewardFailure = ' reward failure displayed',
  RewardToBag = ' reward to bag added',
  FavRewardDetailsView = ' favorite reward details viewed',
  MobileScan = 'mobile scan clicked',
  AccountMenuModalViewed = 'account menu modal viewed',
  RewardBonusClick = 'my rewards-bonuses cta clicked',
  RewardViewClick = ' view clicked',
  GuacModeClick = 'guac mode cta clicked',
  JoinRewardsClick = 'join rewards cta clicked',
  RewardFavorited = ' reward favorited',
  RewardUnfavorited = ' reward unfavorited',
  CategoryFiltered = ' category filtered',
  PointHistoryClick = 'view point history clicked',
  RewardTileClick = ' reward tile clicked',
  FloatingTrackerDetailsClick = ' view details floating tracker clicked',
  ReturnToRewardExchangeClick = 'return to rewards exchange clicked',
  RewardRedeemClick = ' redeem clicked',
  ViewInMyRewardsClick = 'success message view in my rewards clicked',
  DoneSuccessMessageClicked = 'success message done clicked',
  PurchaseError = 'There was a problem getting your reward. purchase error viewed',
  RedemptionPurchaseError = 'Sorry, something went wrong, it is going to take a little longer to deliver your reward. purchase error viewed.',
  ErrorDisplayed = ' error displayed',
  OnBoardingModalView = 'Onboarding modal viewed',
  NextClick = 'next clicked',
  PreviousClick = 'previous click',
  OnBoardingModalClose = 'Onboarding modal closed',
  ViewRewardExchangeCtaClick = 'view rewards exchange cta clicked',
  ViewRewardExchangeClick = 'view rewards exchange clicked',
  GetPointsClick = 'get points cta clicked',
  HowRewardsWorkModalView = 'How Rewards Work modal viewed',
  HowRewardsWorkModalClose = 'How Rewards Work modal closed',
  HowRewardsWorkCreateAccountClick = 'create account cta clicked',
  HowRewardsWorkSignInClick = 'sign in cta clicked',
  HowRewardsWorkCtaClick = 'how rewards works cta clicked',
  RewardsFaqClick = 'rewards faq cta clicked',
  PointRequestClick = 'points request cta clicked',
  RewardsTermsClick = 'chipotle rewards terms clicked',
  JoinNowClick = 'join now cta clicked',
  RewardsStoreModalView = 'Rewards Exchange modal viewed',
  TellMeMoreClick = 'tell me more cta clicked',
  LetsGoClick = `let's go cta clicked`,
  RewardsStoreModalClose = 'Rewards Exchange modal closed',
  CumulativeImpactClick = 'cumulative impact button clicked',
  OrderImpactClick = 'order impact button clicked',
  TwitterShareClick = 'share on twitter clicked',
  RewardsNotificationClaimClick = 'claim cta clicked',
  RewardsNotificationLaterClick = 'later cta clicked',
  RewardsNotificationMyRewardsClick = 'my rewards cta clicked',
  RewardsNotificationCloseClick = 'close cta clicked',
  ContextRewardGetClick = 'get reward clicked',
  ContextRewardRedeemClick = 'redeem clicked',
  ContextRewardRemoveClick = 'remove clicked',
  ContextRewardCheckoutWithRewardClick = 'checkout clicked with contextual reward',
  ContextRewardCheckoutWithNoRewardClick = 'checkout clicked without contextual reward',
}

export enum AnalyticsEventLabel {
  AddressCantDeliver = 'address-cant-deliver',
  BelowMinimumOrder = 'below-minimum-order',
  EstimatedArrivalTimeError = 'estimated-arrival-time-error',
  ItemsCantDeliver = 'items-cant-deliver',
  ModalAddressCantDeliver = 'modal-address-cant-deliver',
  RewardsEnrolled = 'rewards-enrolled',
  RewardsNotEnrolled = 'rewards-non-enrolled',
  TimeCantDeliver = 'time-cant-deliver',
  NapkinsUtensilsSelected = 'napkins-utensils-selected',
  NapkinsUtensilsUnSelected = 'napkins-utensils-unselected',
  CreateGroupOrder = 'create-group-order',
  ViewGroupOrder = 'view-group-order',
  JoinGroupOrder = 'join-group-order',
  CancelGroupOrder = 'cancel-group-order-cta',
  GlobalHeaderPickupDelivery = 'global-header-pickup-from-deliver-to',
  BagPickupDelivery = 'bag-pickup-from-deliver-to',
  CheckoutDelivery = 'checkout-delivery',
  CheckoutPickup = 'checkout-pickup',
  RestaurantSearchPickup = 'restaurant-search-pickup',
  RestaurantSearchPickupCommit = 'restaurant-search-pickup-commit',
  RestaurantSearchDelivery = 'restaurant-search-delivery',
  RestaurantSearchDeliveryCommit = 'restaurant-search-delivery-commit',
  RewardsPageViewed = 'rewards-land|page viewed',
  RewardsRewardExchangeClick = 'rewards-land|rewards exchange clicked',
  ViewPointHistoryClick = 'rewards-land|view points history clicked',
  RewardsForgotToScanClick = 'rewards-land|forgot to scan clicked',
  RewardsLandHowRewardsWorkClick = 'rewards-land|how rewards works clicked',
  RewardsLandFaqClick = 'rewards-land|rewards faq clicked',
  RewardsLandPointRequestClick = 'rewards-land|points request clicked',
  RewardsLandTermsClick = 'rewards-land|chipotle rewards terms clicked',
  AddRewardClick = 'rewards-land|add rewards clicked',
  RewardAdded = 'rewards-land|',
  RewardFailure = 'rewards-land|error-add-rewards|',
  RewardToBag = 'rewards-land|',
  FavRewardDetailsViewed = 'rewards-land|floating-tracker-reward-detail|',
  MobileScan = 'rewards-land|mobile scan clicked',
  AccountMenuModalViewed = 'account-menu|account-menu-modal',
  AccountMenuExchangeClick = 'account-menu|rewards exchange clicked',
  RewardBonusClick = 'account-menu|my rewards & bonuses clicked',
  AccountMenuForgotToScanClick = 'account-menu|forgot to scan clicked',
  RewardViewClick = 'account-menu|modal-view|',
  GuacModeClick = 'account-menu|guac mode clicked',
  JoinRewardsClick = 'account-menu|join rewards clicked',
  RewardStorePageViewed = 'rewards-store|page viewed',
  RewardStoreSpecific = 'rewards-store|',
  PointHistoryClick = 'rewards-store|view point history clicked',
  RewardDetailModalSpecific = 'rewards-detail-modal|',
  ReturnToRewardExchangeClick = 'rewards-detail-modal|return to rewards exchange clicked',
  ViewInMyRewardsClick = 'rewards-detail-modal|success message view in my rewards clicked',
  DoneSuccessMessageClicked = 'rewards-detail-modal|success message done clicked',
  PurchaseError = 'rewards-detail-modal|There was a problem getting your reward',
  RedemptionPurchaseError = 'rewards-detail-modal|Sorry, something went wrong, it is going to take a little longer to deliver your reward',
  RewardPointHistoryPageViewed = 'rewards-point-history|page viewed',
  RewardPointHistoryErrorMessage = 'rewards-point-history|',
  OnBoardingModalViewClose = 'rewards-general-onboard|onboarding modal',
  GeneralOnboardingNextClick = 'rewards-general-onboard|next',
  GeneralOnboardingPreviousClick = 'rewards-general-onboard|previous',
  ViewRewardExchangeClick = 'rewards-general-onboard|view rewards exchange cta clicked',
  GetPointsClick = 'rewards-general-onboard|get points cta retro credit page clicked',
  HowRewardsWorkModal = 'rewards-how-it-works-onboard|How Rewards Work modal',
  HowRewardsWorkNextClick = 'rewards-how-it-works-onboard|next',
  HowRewardsWorkPreviousClick = 'rewards-how-it-works-onboard|previous',
  HowRewardsWorkCreateAccountClick = 'rewards-how-it-works-onboard|create account cta clicked',
  HowRewardsWorkSignInClick = 'rewards-how-it-works-onboard|sign in cta clicked',
  HowRewardsWorkJoinNowClick = 'rewards-how-it-works-onboard|join now cta clicked',
  RewardsStoreModal = 'rewards-store-onboard|Rewards Exchange',
  TellMeMoreClick = 'rewards-store-onboard|tell me more cta clicked',
  LetsGoClick = `rewards-store-onboard|let's go cta clicked`,
  RewardsStoreRewardExchangeClick = 'rewards-store-onboard|view rewards exchange clicked',
  CumulativeImpactClick = 'order-confirmation|sustainability-info-center|cumulative impact clicked',
  OrderImpactClick = 'order-confirmation|sustainability-info-center|order impact clicked',
  TwitterShareClick = 'order-confirmation|sustainability-info-center|share on twitter clicked',
  RewardsNonLoyaltyJoinNowClick = 'rewards-nonloyalty-welcome|join now clicked',
  RewardsNonLoyaltyHowRewardsWorkClick = 'rewards-nonloyalty-welcome|how rewards works clicked',
  RewardsNotificationClaimClick = 'rewards-notification|claim cta clicked',
  RewardsNotificationLaterClick = 'rewards-notification|later cta clicked',
  RewardsNotificationMyRewardsClick = 'rewards-notification|my rewards cta clicked',
  RewardsNotificationCloseClick = 'rewards-notification|close cta clicks',
  ContextRewardGetClick = 'rewards-context-bag|get reward clicked',
  ContextRewardRedeemClick = 'rewards-context-bag|redeem clicked',
  ContextRewardRemoveClick = 'rewards-context-bag|remove clicked',
  ContextRewardCheckoutWithRewardClick = 'rewards-context-bag|checkout clicked with contextual reward',
  ContextRewardCheckoutWithNoRewardClick = 'rewards-context-bag|checkout clicked without contextual reward'
}

const SCROLL_PERCENTAGE_INCREMENT = 10;

export class AppGoogleAnalyticsService implements GoogleAnalyticsService {
  lastVisitedProductDetailView: string = '';
  dataLayer: Array<any> = [];
  loadingScriptPromise: Promise<void>;
  lastScrollPercentage = 0;

  constructor(private config: Config, private metadataService: MetadataService, private store: Store<AppState>, protected logger: Logger) {
    const scriptUrl = `https://www.googletagmanager.com/gtm.js?id=${this.config.google.analyticsId}`;
    this.loadingScriptPromise = loadScript(scriptUrl);

    this.loadingScriptPromise
      .then(() => {
        const win = window as any;
        win.dataLayer = win.dataLayer || [];
        this.dataLayer = win.dataLayer;

        // This initial push event is absolutely critical for click events to fire
        this.sendEvent({ 'gtm.start': new Date().getTime(), event: 'gtm.js' });
      })
      .catch(error => {
        this.logger.error('Could not load Google analytics script', error);
      });
  }

  sendCustomEvent(event: CustomAnalyticsEvent): void {
    this.sendEvent(
      {
        event: 'customEvent',
        customEventCategory: event.category,
        customEventAction: event.action,
        customEventLabel: event.label,
        ...(event.values ?? {}),
      },
      ['Could not send Google analytics event', event],
    );
  }

  sendPageViewEvent(page: string): void {
    this.sendEvent({ event: AnalyticsEventAction.VirtualPageView, page }, [`Could not send Google Analytics pageview ${page}`]);
  }

  sendLoyaltyEnrollmentEvent(customer: CustomerProfile | null, pageTitle?: string): void {
    if (!customer) {
      return;
    }

    const loyaltySubscription = customer.subscriptions.find(s => s.context == 'loyalty');

    if (loyaltySubscription) {
      const eventAction = loyaltySubscription.status == CustomerSubscriptionStatus.Subscribed ? AnalyticsEventAction.EnrollSuccess : AnalyticsEventAction.EnrollPendingSuccess;
      this.sendCustomEvent({ category: AnalyticsEventCategory.Rewards, action: eventAction, label: pageTitle });
    }
  }

  async sendEcommerceTransactionEvent(orderWithContext: OrderWithContext, customer: CustomerProfile, enrolledInLoyalty: boolean, currencyCode: string = 'USD'): Promise<void> {
    const order = orderWithContext.order;
    let products: AnalyticsProduct[] = [];

    if (order) {
      const meals = order.meals;
      const nonFoodItems = order.nonFoodItems;

      if (meals) {
        const metadata = (await this.metadataService.getMenuMetadata()).metadata;
        const lifeStyleMeals = this.getLifestyleMeals();
        products = convertMealsToAnalyticsProducts(meals, metadata, this.store.state.menu.menuItemLookupMap!, this.store.state.menu.activeMenu, lifeStyleMeals);
      }

      if (nonFoodItems) {
        products = products.concat(convertNonFoodItemsToAnalyticsProducts(nonFoodItems));
      }

      this.sendEvent({
        event: 'transaction',
        orderType: order.delivery ? 'Delivery' : 'Pickup',
        customerType: customer.isGuest ? 'Guest' : 'Account',
        customerRewards: enrolledInLoyalty ? 'Enrolled' : 'Non-enrolled',
        partyType: order.orderType == OrderType.Group ? 'Group' : 'Individual',
        tip: order.orderTipAmount,
        feeTotal: order.delivery ? Number((order.taxesAndFees.deliveryServiceFee + order.delivery.feeAmount).toFixed(2)) : 0,
        discount: order.orderDiscountAmount,
        mealsOrdered: reduceProductsToString(products),
        totalQuantity: size(meals),
        subtotal: order.orderMealsExtendedPrice,
        restaurantId: order.restaurantId,
        groupOrderID: order.groupOrderId,
        ecommerce: {
          currencyCode,
          purchase: {
            actionField: {
              id: order.orderId,
              revenue: order.orderTotalAmount,
              tax: order.orderTaxAmount,
            },
            products,
          },
        },
      });
    }
  }

  sendProductClickEvent(productName: string): void {
    this.sendEvent({
      event: 'product-click',
      ecommerce: {
        click: {
          products: [{ name: productName }],
        },
      },
    });
  }

  sendProductDetailViewEvent(productName: string): void {
    this.lastVisitedProductDetailView = productName;
    this.sendEvent({
      event: 'product-detail-view',
      ecommerce: {
        detail: {
          products: [{ name: productName }],
        },
      },
    });
  }

  sendAddToBagEvent(products: AnalyticsProduct[], currencyCode: string = 'USD'): void {
    this.sendEvent({
      event: AnalyticsEventAction.AddToBag,
      ecommerce: {
        currencyCode,
        add: {
          products,
        },
      },
    });
  }

  sendScrollEvent(scrollPercentage: number): void {
    this.sendEvent({
      event: AnalyticsEventAction.ScrollEvent,
      attributes: {
        distance: scrollPercentage,
      },
    });
  }

  trackElementScrollPercentage(element: HTMLElement): void {
    element.addEventListener('scroll', e => {
      const currentScrollPercentage = (element.scrollTop / (element.scrollHeight - element.clientHeight)) * 100;
      if (currentScrollPercentage >= this.lastScrollPercentage + SCROLL_PERCENTAGE_INCREMENT) {
        this.lastScrollPercentage += SCROLL_PERCENTAGE_INCREMENT;
        this.sendScrollEvent(this.lastScrollPercentage);
      }
    });
  }

  resetLastScrollPercentage(): void {
    this.lastScrollPercentage = 0;
  }

  recentOrFavoriteMealAddedToBag(meal: RecentOrFavoriteOrderMeal): void {
    const products = convertMealRequestOrRecentOrFavoriteMealToAnalyticsProducts(meal, this.store.state.menu.activeMenu);
    this.sendAddToBagEvent(products);
  }

  async preconfigureMealAddedToBag(meal: PreConfiguredMeal): Promise<void> {
    const metadata = (await this.metadataService.getMenuMetadata()).metadata;
    const products = convertPreconfiguredMealToAnalyticsProducts(meal, metadata, this.store.state.menu.menuItemLookupMap!);
    this.sendAddToBagEvent(products);
  }

  builtMealAddedToBag(meal: OrderMealRequest): void {
    const products = convertMealRequestOrRecentOrFavoriteMealToAnalyticsProducts(meal.meal, this.store.state.menu.activeMenu);
    this.sendAddToBagEvent(products);
  }

  async sendEcommerceCheckoutEvent(order: OrderWithContext, stepName: string, step: number): Promise<void> {
    let products: AnalyticsProduct[] = [];

    if (order) {
      const meals = order.order.meals;

      if (meals) {
        const metadata = (await this.metadataService.getMenuMetadata()).metadata;
        const lifeStyleMeals = this.getLifestyleMeals();
        products = convertMealsToAnalyticsProducts(meals, metadata, this.store.state.menu.menuItemLookupMap!, this.store.state.menu.activeMenu, lifeStyleMeals);
      }
    }

    this.addCheckoutEventToDataLayer(step, stepName, products);
  }

  private async sendEvent(event: { event: string; [key: string]: any }, errorLoggerParams?: Parameters<Logger['error']>): Promise<void> {
    try {
      await this.loadingScriptPromise;
      const isLoggedIn = this.store.getters[CUSTOMER_GETTERS.IS_LOGGED_IN] ?? false;
      this.dataLayer.push({ ...event, isLoggedIn });
    } catch {
      if (errorLoggerParams) {
        this.logger.error(...errorLoggerParams);
      }
    }
  }

  private getLifestyleMeals(): PreConfiguredMeal[] {
    return this.store.state.menu.preConfiguredMeals.filter(x => x.mealType == MealType.Lifestyle);
  }

  private addCheckoutEventToDataLayer(step: number, stepName: string, products: AnalyticsProduct[] = [], currencyCode: string = 'USD'): void {
    this.sendEvent({
      event: 'checkout-step',
      stepName,
      ecommerce: {
        currencyCode,
        checkout: {
          actionField: {
            step,
            option: '',
          },
          products,
        },
      },
    });
  }
}
